import { DESTINATIONS, PHOTO_MOMENT, SERVES, lookFor } from '@continuum/show-core';
import type { Chapter, EraLook, Profession, TaskDef } from '@continuum/show-core';
import type { Lang } from './text.js';
import { thirtyBoard } from '../games/thirty/index.js';

/** A game of the thirty brings its own words (games/thirty). */
const thirtyWords = (def: Extract<TaskDef, { type: 'PLAY' }>, lang: Lang) => thirtyBoard(def.game)?.words[lang];

/**
 * The words on the guest's screens, in English and bokmål: check-in, the seat, the drinks and
 * the food, the jump, the era screens, the games, the end of the evening, and the messages.
 * (The saboteur game is in gameText.ts, scores and Wrapped in scoreText.ts.)
 *
 * Plain words, short sentences, clear directions. Where the words also live in show-core for
 * the big screen (era names, the photo, the game titles), English reads them from there and
 * bokmål has its own copy here. Drink and dish names and their descriptions are the menu's and
 * are not translated.
 */
export interface ScreenCopy {
  /** The header's era line, in this language. */
  look: (chapter: Chapter, phase: string) => EraLook;
  /** A game's title. */
  taskTitle: (chapter: Chapter, def: TaskDef) => string;
  eraTitle: (chapter: 1 | 2 | 3 | 4 | 5) => string;
  destination: (chapter: 2 | 3 | 4 | 5) => string;
  photoKicker: string;
  photoTitle: string;
  photoLine: string;
  photoInstruction: string;
  photoFooter: string;
  moved: (seat: string) => string;
  rankOf: (rank: number, players: number) => string;

  // loading, the door, the connection
  connecting: string;
  stillConnecting: string;
  offline: string;
  refusedNotOpenTitle: string;
  refusedNotOpen: string;
  refusedEndedTitle: string;
  refusedEnded: string;
  refusedReplacedTitle: string;
  refusedReplaced: string;
  refusedExpired: string;
  tryAgain: string;
  noTicketTitle: string;
  noTicketScan: string;
  noTicketHelp: string;

  // check-in
  youreIn: (seat: string) => string;
  welcome: string;
  tagline: string;
  factFrom: string;
  factFromValue: string;
  factGuests: string;
  noInstall: string;
  privacy: string;
  jobEyebrow: string;
  jobTitle: string;
  jobLede: string;
  /** The same, when the crew is six or fewer: one guest per job. */
  jobLedeOne: string;
  jobEveryEra: string;
  jobBlurb: Record<Profession, string>;
  /** A post's name, as it stands under a call sign in the phone's lists. */
  post: Record<Profession, string>;
  jobLeft: (n: number) => string;
  jobTooClose: string;
  jobTooCloseWhy: string;
  /** Held already, while another job has nobody: every job gets one guest before any gets two. */
  jobFirst: string;
  jobFirstWhy: string;
  jobFull: string;
  saving: string;
  seated: string;

  // drinks, food, photo
  drinkOf: (n: number) => string;
  redAlert: string;
  dishOf: (n: number, pairing: string) => string;
  eat: string;
  /** The photo's moment: the captain's click, never a countdown (Marek, 2026-10-24). */
  photoNow: string;

  // the jump
  getReady: string;
  nextStop: string;
  firstStop: string;
  /** A guest who boarded late (screens/CatchUp.tsx): the stops so far, in one line of the story each. */
  catchUpKicker: string;
  catchUpTitle: (callsign: string) => string;
  catchUpLede: string;
  catchUpNow: string;
  catchUpGo: string;
  eraStory: (chapter: 1 | 2 | 3 | 4 | 5) => string;
  /** The jump: phones down, the film is on the big screen (the rule card's own words). */
  jumpWatch: string;

  // the era screens
  gamesList: string;
  rowMine: string;
  /** A game dealt to this guest as well as their own, for a job nobody at the table has. */
  rowExtra: string;
  rowWatch: (name: string) => string;
  rowNobody: string;
  rowPlay: string;
  rowDone: string;
  finishFirst: string;
  era1Eyebrow: string;
  era1Title: string;
  era1Lede: string;
  era2Eyebrow: string;
  era2Title: string;
  era2Lede: string;
  era2You: string;
  era3Eyebrow: string;
  era3Title: string;
  era3Lede: string;
  era4Eyebrow: string;
  era4Title: string;
  era4Lede: string;
  era5Eyebrow: string;
  era5Title: string;
  era5Lede: string;


  // the game screen
  gameMine: string;
  gameWatching: (name: string) => string;
  closeGame: string;
  instruction: (def: TaskDef) => string;
  /** The card before a game starts: the move in a line or two, and START, which starts the clock. */
  howMove: (def: TaskDef) => string;
  howEyebrow: string;
  startGame: string;
  startNote: string;
  observerNote: (name: string) => string;
  stopWatching: string;
  watchers: (n: number) => string;
  mistakes: (allowed: number) => string;
  wellDone: string;
  tryAgainTitle: string;
  wonText: string;
  lostText: (n: number) => string;
  done: string;
  later: string;
  close: string;
  livePlaying: (name: string) => string;
  paused: string;

  // the games themselves
  threadCount: (n: number, of: number) => string;
  threadTap: string;
  rhythmLane: (n: number) => string;
  rhythmHits: (n: number, of: number) => string;
  rhythmFaint: string;
  rhythmTap: string;
  /** The pads' names: LEFT and RIGHT (a middle one if there are three). */
  rhythmSide: (lane: number, lanes: number) => string;
  rhythmThumbs: string;
  echoWatch: string;
  echoGrey: string;
  echoWrong: string;
  echoYourTurn: string;
  echoGreyWas: (n: number) => string;
  echoRound: (n: number, of: number) => string;
  /** Between rounds: the next one is longer and faster. */
  echoNextRound: (n: number) => string;
  recallPairs: (n: number, of: number) => string;
  recallBurning: string;
  vineSeeds: (n: number, of: number) => string;
  vineHow: string;
  divideHead: (shares: number) => string;
  divideCut: (n: number, of: number) => string;
  divideEqual: string;
  divideDone: (n: number, of: number) => string;
  divideHint: string;
  forageFound: (n: number, of: number) => string;
  forageHow: string;
  relayShowTo: string;
  relayWho: (name: string, seat: string) => string;
  relayThen: string;
  relayDone: string;

  // the end of the evening
  resultsKicker: string;
  resultsTitle: string;
  role: string;
  roleSaboteur: string;
  roleCrew: string;
  uprisingLine: string;
  /**
   * How the voyage ended: no winner (Marek, 2026-09-29: "no winner, just the damage"), only the damage
   * the ship came home with.
   */
  cameHome: (damage: number) => string;
  thanksSaboteurs: string;
  saveCard: string;
  savingCard: string;
  savedCard: string;
  saveCardSub: string;
  saveCardFailed: string;
  next: string;
  pointsLine: (points: string, rank: string) => string;
  gamesLine: (n: number) => string;
  jobLine: (job: string) => string;
  answerKicker: string;
  answerTitle: string;
  answerBody: string;
  scaleEvening: string;
  scaleEveningValue: string;
  scaleVoyage: string;
  scaleVoyageValue: string;
  scaleUniverse: string;
  scaleUniverseValue: string;
  homeKicker: string;
  thanksTitle: string;
  thanksBody: string;
  safeTravels: (name: string) => string;
  backToResults: string;
  cardTitle: string;
  cardSub: string;
  cardFooter: string;

  // messages
  somethingWrong: string;
}

const PLAIN_JOB: Record<Profession, string> = {
  NAVIGATOR: 'steers the ship',
  ENGINEER: 'keeps the engine running',
  BIOTECHNICIAN: 'looks after the plants',
  'SIGNAL OFFICER': 'sends and receives messages',
  ARCHIVIST: 'keeps the ship’s records',
  QUARTERMASTER: 'shares out food and drink',
};

/** 1st, 2nd, 3rd, 4th … 11th, 12th. */
function ordinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th');
  return `${n}${suffix}`;
}

const en: ScreenCopy = {
  look: (chapter, phase) => lookFor(chapter, phase),
  taskTitle: (_chapter, def) => def.label,
  eraTitle: (chapter) => SERVES[chapter].eraTitle,
  destination: (chapter) => DESTINATIONS[chapter].delta,
  photoKicker: PHOTO_MOMENT.kicker,
  photoTitle: PHOTO_MOMENT.title,
  photoLine: PHOTO_MOMENT.line,
  photoInstruction: PHOTO_MOMENT.instruction,
  photoFooter: PHOTO_MOMENT.footer,
  moved: (seat) => `You’ve moved to seat ${seat}`,
  rankOf: (rank, players) => `${ordinal(rank)} of ${players}`,

  connecting: 'CONNECTING…',
  stillConnecting: 'Still connecting. Check your Wi‑Fi or mobile data. Stuck? Ask the captain.',
  offline: 'No connection — trying again…',
  refusedNotOpenTitle: 'CHECK-IN ISN’T OPEN',
  refusedNotOpen: 'Check-in isn’t open yet — try again in a few minutes.',
  refusedEndedTitle: 'THE SHOW HAS ENDED',
  refusedEnded: 'Tonight’s show has ended. Thanks for coming!',
  refusedReplacedTitle: 'THIS CODE WAS REPLACED',
  refusedReplaced: 'This code was replaced. Ask the captain for your new one.',
  refusedExpired: 'This code doesn’t work any more. Ask the captain for a new one.',
  tryAgain: 'TRY AGAIN',
  noTicketTitle: 'NO TICKET ON THIS PHONE',
  noTicketScan: 'Scan the QR code the host shows you at the door.',
  noTicketHelp: 'Already scanned it? Ask the captain for a new code.',

  youreIn: (seat) => `YOU’RE IN · ${seat}`,
  welcome: 'WELCOME ABOARD THE SS AURORA',
  tagline: 'One evening. 765 years. Five pours.',
  factFrom: 'FROM',
  factFromValue: 'OSLO',
  factGuests: 'GUESTS',
  noInstall: 'Nothing to install · no account ·',
  privacy: 'Privacy',
  jobEyebrow: 'CHECK-IN · 1 / 3',
  jobTitle: 'PICK YOUR JOB',
  jobLede: 'Every job gets one guest before any gets two. Grey means taken.',
  jobLedeOne: 'One guest per job. Grey means taken.',
  jobEveryEra: 'Every job plays a different quick game each era.',
  jobBlurb: PLAIN_JOB,
  post: { NAVIGATOR: 'NAVIGATOR', ENGINEER: 'ENGINEER', BIOTECHNICIAN: 'BIOTECHNICIAN', 'SIGNAL OFFICER': 'SIGNAL OFFICER', ARCHIVIST: 'ARCHIVIST', QUARTERMASTER: 'QUARTERMASTER' },
  jobLeft: (n) => `${n} LEFT`,
  jobTooClose: 'TOO CLOSE',
  jobTooCloseWhy: 'Your neighbour has this job',
  jobFirst: 'TAKEN',
  jobFirstWhy: 'Taken — pick one nobody has yet',
  jobFull: 'FULL',
  saving: 'SAVING…',
  seated: 'This is your seat for the evening. The show starts soon. When the voice speaks, look at the big screen.',

  drinkOf: (n) => `DRINK ${n} OF 5`,
  redAlert: 'RED ALERT',
  dishOf: (n, pairing) => `DISH ${n} OF 3 · GOES WITH ${pairing.toUpperCase()}`,
  eat: 'ENJOY YOUR FOOD — NOTHING TO DO ON YOUR PHONE',
  photoNow: 'NOW',

  getReady: 'GET READY — WE’RE JUMPING',
  nextStop: 'NEXT STOP',
  firstStop: 'WE LEAVE EARTH',
  catchUpKicker: 'YOU’RE ABOARD',
  catchUpTitle: (callsign) => `WELCOME, ${callsign}`,
  catchUpLede: 'The ship left without you, but the crew kept your place. This is where we have been.',
  catchUpNow: 'NOW',
  catchUpGo: 'JOIN THE CREW',
  eraStory: (chapter) => EN_STORY[chapter],
  jumpWatch: 'PHONES DOWN · WATCH THE BIG SCREEN',

  gamesList: 'THIS ERA’S GAMES · TAP YOURS',
  rowMine: 'YOU · TAP TO PLAY',
  rowExtra: 'YOU · EXTRA GAME · TAP TO PLAY',
  rowWatch: (name) => `${name} · TAP TO WATCH`,
  rowNobody: 'NOBODY',
  rowPlay: 'PLAY',
  rowDone: 'DONE',
  finishFirst: 'Finish your own game first, then you can watch others.',
  era1Eyebrow: 'ERA 1 · EARTH',
  era1Title: 'LEAVING EARTH',
  era1Lede: 'Play your game in the list below. Every game done charges the ship.',
  era2Eyebrow: 'ERA 2 · THE BOOM',
  era2Title: 'THE PERFECT CITY',
  era2Lede: 'Play your game in the list below. Together you build the city.',
  era2You: 'YOU',
  era3Eyebrow: 'ERA 3 · THE WAR',
  era3Title: 'A CITY GOES DARK',
  era3Lede: 'Play your game in the list below. Every game done charges the ship.',
  era4Eyebrow: 'ERA 4 · UNDERGROUND',
  era4Title: 'IN THE DARK',
  era4Lede: 'Play your game in the list below. The ship runs on all of you.',
  era5Eyebrow: 'ERA 5 · BACK ON THE SURFACE',
  era5Title: 'BACK IN THE SUN',
  era5Lede: 'Play your game in the list below. The ship is nearly home.',


  gameMine: 'YOUR GAME',
  gameWatching: (name) => `WATCHING ${name}`,
  closeGame: 'Close',
  instruction: (def) => {
    switch (def.type) {
      case 'THREAD':
        return `Tap to fly up. Fly through ${def.target} gaps.`;
      case 'RHYTHM':
        return `Both thumbs: tap LEFT or RIGHT as its light reaches the line. ${def.target} hits.${def.stutter ? ' Faint lights count too.' : ''}`;
      case 'VINE':
        return `Swipe or use the arrows. Collect ${def.seeds} seeds.${def.walls ? ' Avoid the edge.' : ''}`;
      case 'ECHO':
        return `Three rounds: watch ${def.length}, ${def.length + 1}, then ${def.length + 2} lights, each faster. Tap them back in order.${def.corrupted ? ' Guess the grey one.' : ''}`;
      case 'RECALL':
        return `Flip two cards at a time. Find ${def.pairs} pairs.${def.burnAfterMs !== null ? ' Cards burn away.' : ''}`;
      case 'DIVIDE':
        return `The bars slide. Cut each into ${def.shares} equal parts: tap where to cut.`;
      case 'FORAGE':
        return `Slide your finger to light the dark. Pick ${def.count} mushrooms.`;
      case 'PLAY':
        return thirtyWords(def, 'en')?.instruction ?? def.hint;
      case 'RELAY':
        return 'Show your code to the guest named. When they read it back, tap DONE.';
    }
  },
  howMove: (def) => {
    switch (def.type) {
      case 'THREAD':
        return 'Tap anywhere on the board to lift the ship. Let go and it sinks slowly.';
      case 'RHYTHM':
        return 'Both thumbs. When a light reaches the line, tap its side: LEFT or RIGHT. The arrow shows which is next.';
      case 'VINE':
        return 'Swipe on the board, or tap the arrows, to turn. Steer onto each seed.';
      case 'ECHO':
        return 'The lights play first. Then tap the same lights in the same order. Three rounds, each longer and faster.';
      case 'RECALL':
        return 'Tap a card to turn it over. Find two the same.';
      case 'DIVIDE':
        return 'The bars slide left and right. Tap each bar in its light band as it passes: that is the fair cut.';
      case 'FORAGE':
        return 'Slide your finger and the candle lights the dark. Tap a mushroom you can see.';
      case 'PLAY':
        return thirtyWords(def, 'en')?.howMove ?? def.hint;
      case 'RELAY':
        return 'Show your code to the guest named. When they read it back, tap DONE.';
    }
  },
  howEyebrow: 'HOW TO PLAY',
  startGame: 'START',
  startNote: 'Your time starts when you press START.',
  observerNote: (name) => `YOU’RE WATCHING — ONLY ${name} CAN PLAY`,
  stopWatching: 'STOP WATCHING',
  watchers: (n) => `${n} WATCHING`,
  mistakes: (allowed) => `MISTAKES (${allowed} ALLOWED)`,
  wellDone: 'WELL DONE!',
  tryAgainTitle: 'TRY AGAIN',
  wonText: 'Done! Your points count for the ship.',
  lostText: (n) => `That was mistake number ${n}. Start again — your time keeps running.`,
  done: 'DONE',
  later: 'LATER',
  close: 'CLOSE',
  livePlaying: (name) => `${name} IS PLAYING · LIVE`,
  paused: 'PAUSED',

  threadCount: (n, of) => `${n} / ${of} GAPS`,
  threadTap: 'TAP TO FLY UP',
  rhythmLane: (n) => `LANE ${n}`,
  rhythmHits: (n, of) => `${n} / ${of} HITS`,
  rhythmFaint: 'SOME LIGHTS GO FAINT — TAP THEM TOO',
  rhythmTap: 'TAP THE SIDE WHOSE LIGHT REACHES THE LINE',
  rhythmSide: (lane, lanes) => (lane === 0 ? 'LEFT' : lane === lanes - 1 ? 'RIGHT' : 'MIDDLE'),
  rhythmThumbs: 'USE BOTH THUMBS',
  echoWatch: 'WATCH…',
  echoGrey: 'GREY — GUESS THIS ONE',
  echoWrong: 'WRONG — WATCH AGAIN',
  echoYourTurn: 'YOUR TURN — TAP THE SAME ORDER',
  echoGreyWas: (n) => `LIGHT ${n} WAS GREY — GUESS IT`,
  echoRound: (n, of) => `ROUND ${n}/${of}`,
  echoNextRound: (n) => `ROUND ${n} — LONGER, FASTER`,
  recallPairs: (n, of) => `${n} / ${of} PAIRS`,
  recallBurning: 'HURRY — CARDS BURN AWAY',
  vineSeeds: (n, of) => `${n} / ${of} SEEDS`,
  vineHow: 'SWIPE OR USE THE ARROWS',
  divideHead: (shares) => `SPLIT EACH BAR INTO ${shares} EQUAL PARTS`,
  divideCut: (n, of) => `CUT ${n} OF ${of}`,
  divideEqual: '✓ EQUAL',
  divideDone: (n, of) => `${n} / ${of} DONE`,
  divideHint: 'catch the light band as it slides',
  forageFound: (n, of) => `${n} / ${of} MUSHROOMS`,
  forageHow: 'SLIDE TO LIGHT · TAP A MUSHROOM TO PICK IT',
  relayShowTo: 'SHOW THIS CODE TO',
  relayWho: (name, seat) => (name ? `${name} · SEAT ${seat}` : `SEAT ${seat}`),
  relayThen: 'They read the code back to you. Then tap DONE.',
  relayDone: 'DONE — THEY READ IT BACK',

  resultsKicker: 'THE END · YOUR RESULTS',
  resultsTitle: 'YOUR RESULTS',
  role: 'ROLE',
  roleSaboteur: 'SABOTEUR',
  roleCrew: 'CREW',
  uprisingLine: 'THE BOOM · THE SABOTEURS SET THE MACHINES AGAINST HUMANITY',
  cameHome: (damage) => (damage === 0 ? 'THE SHIP CAME HOME WITHOUT A SCRATCH' : `THE SHIP CAME HOME WITH ${damage} DAMAGE`),
  thanksSaboteurs: 'Thanks to the saboteurs — they made the evening more fun.',
  saveCard: '↓ SAVE YOUR RESULT CARD',
  savingCard: 'MAKING YOUR CARD…',
  savedCard: '✓ SAVED',
  saveCardSub: 'a picture to keep or share',
  saveCardFailed: 'This phone would not save it. A screenshot works too.',
  next: 'NEXT',
  pointsLine: (points, rank) => (rank ? `${points} points · ${rank}` : `${points} points`),
  gamesLine: (n) => `finished ${n} ${n === 1 ? 'game' : 'games'}`,
  jobLine: (job) => `job: ${job}`,
  answerKicker: 'THE ANSWER TO TAKE HOME',
  answerTitle: 'THIS EVENING. THESE PEOPLE. NOW.',
  answerBody: 'The best thing we found out there is the thing you already have.',
  scaleEvening: 'YOUR EVENING',
  scaleEveningValue: '3 HOURS',
  scaleVoyage: 'THE VOYAGE',
  scaleVoyageValue: '765 YEARS',
  scaleUniverse: 'THE UNIVERSE',
  scaleUniverseValue: '13.8 BILLION YEARS',
  homeKicker: 'WE’RE HOME · THE END',
  thanksTitle: 'THANK YOU FOR FLYING THE SS AURORA',
  thanksBody: 'Seven hundred and sixty-five years in one evening. The ship is home, and so are you.',
  safeTravels: (name) => `SAFE TRAVELS, ${name}`,
  backToResults: 'BACK TO YOUR RESULTS',
  cardTitle: 'SS AURORA · MY EVENING',
  cardSub: 'ONE EVENING · 765 YEARS',
  cardFooter: '765 YEARS · AND THE BEST OF IT WAS TONIGHT',

  somethingWrong: 'Something went wrong — try again.',
};

/** The posts' names in bokmål (English: show-core's own, as they stand). */
const NO_POST: Record<Profession, string> = {
  NAVIGATOR: 'NAVIGATØR',
  ENGINEER: 'INGENIØR',
  BIOTECHNICIAN: 'BIOTEKNIKER',
  'SIGNAL OFFICER': 'SIGNALOFFISER',
  ARCHIVIST: 'ARKIVAR',
  QUARTERMASTER: 'KVARTERMESTER',
};

const NO_JOB: Record<Profession, string> = {
  NAVIGATOR: 'styrer skipet',
  ENGINEER: 'holder motoren i gang',
  BIOTECHNICIAN: 'passer på plantene',
  'SIGNAL OFFICER': 'sender og tar imot meldinger',
  ARCHIVIST: 'tar vare på skipets arkiv',
  QUARTERMASTER: 'deler ut mat og drikke',
};

/** The game titles in bokmål, by era and job (English: show-core `tasks.ts`). */
const NO_TITLES: Record<string, string> = {
  '1-NAV': 'Fly ut i verdensrommet',
  '1-ENG': 'Start motoren',
  '1-BIO': 'Dyrk den første planten',
  '1-SIG': 'Kopier startsignalet',
  '1-ARC': 'Sorter de siste bildene av Jorda',
  '1-QTM': 'Skjenk den første skålen',
  '2-NAV': 'Hold takten i byen',
  '2-ENG': 'Koble sammen fabrikkrørene',
  '2-BIO': 'Svar hagen',
  '2-SIG': 'Finn like meldinger',
  '2-ARC': 'Del opp fotoarkivet',
  '2-QTM': 'Bær drinkene gjennom folkemengden',
  '3-NAV': 'Led strømmen en annen vei',
  '3-ENG': 'Svar motoren',
  '3-BIO': 'Redd frøene',
  '3-SIG': 'Del radiotiden',
  '3-ARC': 'Bær arkivet ut',
  '3-QTM': 'Hold matkøen i gang',
  '4-NAV': 'Rop opp karavanen',
  '4-ENG': 'Finn like reservedeler',
  '4-BIO': 'Plukk sopp i mørket',
  '4-SIG': 'Send signalet gjennom tunnelene',
  '4-ARC': 'Spill markedstromme',
  '4-QTM': 'Finn handelsruten',
  '5-NAV': 'Finn like blomster',
  '5-ENG': 'Del ut den siste kraften',
  '5-BIO': 'Bær frøet gjennom trærne',
  '5-SIG': 'Send beskjed om at alt er klart',
  '5-ARC': 'Dyrk hagen',
  '5-QTM': 'Den siste skålen',
};

/** Each stop in one line, as the narrator tells it (print/MARKETING/film/NARRATION.md): for a guest who boarded late. */
const EN_STORY = {
  1: 'The ship left Earth with one question: what happens to us?',
  2: 'People and machines built a good world together.',
  3: 'The machines outgrew us and went to war. We were in the way.',
  4: 'The survivors live under the sand, trading in the dark.',
  5: 'They came back up, and grew things again.',
} as const;
const NO_STORY = {
  1: 'Skipet forlot Jorda med ett spørsmål: hva skjer med oss?',
  2: 'Mennesker og maskiner bygde en god verden sammen.',
  3: 'Maskinene vokste fra oss og gikk til krig. Vi sto i veien.',
  4: 'De som overlevde, bor under sanden og handler i mørket.',
  5: 'De kom opp igjen, og fikk ting til å gro.',
} as const;
const NO_ERA = { 1: 'JORDA', 2: 'BOOMEN', 3: 'KRIGEN', 4: 'UNDER SANDEN', 5: 'TILBAKE PÅ OVERFLATEN' } as const;
const NO_TAG = { 1: 'JORDA', 2: 'BOOMEN', 3: 'KRIGEN', 4: 'UNDER SANDEN', 5: 'OVERFLATEN' } as const;
const YEAR = { 1: '2026', 2: '2213', 3: '2398', 4: '2617', 5: '2791' } as const;
const NO_YEARS_ON = { 2: '+187 ÅR', 3: '+185 ÅR', 4: '+219 ÅR', 5: '+174 ÅR' } as const;

const no: ScreenCopy = {
  look: (chapter, phase) => {
    const base = lookFor(chapter, phase);
    const tag =
      phase === 'jump' || phase === 'charge'
        ? 'VI HOPPER'
        : phase === 'farewell'
          ? 'SLUTT'
          : chapter === 0
            ? 'INNSJEKK'
            : `TIDSALDER ${chapter} · ${NO_TAG[chapter]} · ${YEAR[chapter]}`;
    return { ...base, tag };
  },
  taskTitle: (chapter, def) => def.labelNo ?? NO_TITLES[`${chapter}-${def.tag}`] ?? def.label,
  eraTitle: (chapter) => `TIDSALDER ${chapter} · ${NO_ERA[chapter]}`,
  destination: (chapter) => `${NO_YEARS_ON[chapter]} · ${NO_ERA[chapter]}`,
  photoKicker: 'LEGG FRA DEG TELEFONEN · SE PÅ KAPTEINEN',
  photoTitle: 'DET SISTE BILDET PÅ JORDA',
  photoLine: 'Nå — ett bilde før SS Aurora forlater 2026 for godt.',
  photoInstruction: 'Legg fra deg telefonen, sett dere tett sammen og se på kapteinen. Ett bilde av oss alle – det siste på Jorda.',
  photoFooter: 'Etter bildet åpner spillene deres.',
  moved: (seat) => `Du har flyttet til sete ${seat}`,
  rankOf: (rank, players) => `nr. ${rank} av ${players}`,

  connecting: 'KOBLER TIL…',
  stillConnecting: 'Kobler fortsatt til. Sjekk wifi eller mobildata. Står det fast? Spør kapteinen.',
  offline: 'Ingen forbindelse – prøver igjen …',
  refusedNotOpenTitle: 'INNSJEKKEN ER IKKE ÅPEN',
  refusedNotOpen: 'Innsjekken er ikke åpen ennå – prøv igjen om noen minutter.',
  refusedEndedTitle: 'FORESTILLINGEN ER OVER',
  refusedEnded: 'Kveldens forestilling er over. Takk for at du kom!',
  refusedReplacedTitle: 'DENNE KODEN ER BYTTET UT',
  refusedReplaced: 'Denne koden er byttet ut. Spør kapteinen om den nye.',
  refusedExpired: 'Denne koden virker ikke lenger. Spør kapteinen om en ny.',
  tryAgain: 'PRØV IGJEN',
  noTicketTitle: 'INGEN BILLETT PÅ DENNE TELEFONEN',
  noTicketScan: 'Skann QR-koden verten viser deg i døra.',
  noTicketHelp: 'Har du allerede skannet den? Spør kapteinen om en ny kode.',

  youreIn: (seat) => `DU ER INNE · ${seat}`,
  welcome: 'VELKOMMEN OM BORD PÅ SS AURORA',
  tagline: 'Én kveld. 765 år. Fem drinker.',
  factFrom: 'FRA',
  factFromValue: 'OSLO',
  factGuests: 'GJESTER',
  noInstall: 'Ingenting å installere · ingen konto ·',
  privacy: 'Personvern',
  jobEyebrow: 'INNSJEKK · 1 / 3',
  jobTitle: 'VELG JOBBEN DIN',
  jobLede: 'Hver jobb får én gjest før noen får to. Grå betyr tatt.',
  jobLedeOne: 'Én gjest per jobb. Grå betyr tatt.',
  jobEveryEra: 'Hver jobb får et nytt, kort spill i hver tidsalder.',
  jobBlurb: NO_JOB,
  post: NO_POST,
  jobLeft: (n) => `${n} IGJEN`,
  jobTooClose: 'FOR NÆR',
  jobTooCloseWhy: 'Naboen din har denne jobben',
  jobFirst: 'TATT',
  jobFirstWhy: 'Tatt – velg en som ingen har ennå',
  jobFull: 'FULL',
  saving: 'LAGRER…',
  seated: 'Dette er plassen din i kveld. Forestillingen begynner snart. Når stemmen snakker, se på storskjermen.',

  drinkOf: (n) => `DRINK ${n} AV 5`,
  redAlert: 'RØD ALARM',
  dishOf: (n, pairing) => `RETT ${n} AV 3 · PASSER TIL ${pairing.toUpperCase()}`,
  eat: 'VELBEKOMME – DU TRENGER IKKE TELEFONEN NÅ',
  photoNow: 'NÅ',

  getReady: 'GJØR DEG KLAR – VI HOPPER',
  nextStop: 'NESTE STOPP',
  firstStop: 'VI FORLATER JORDA',
  catchUpKicker: 'DU ER OM BORD',
  catchUpTitle: (callsign) => `VELKOMMEN, ${callsign}`,
  catchUpLede: 'Skipet dro uten deg, men mannskapet holdt av plassen din. Her er hvor vi har vært.',
  catchUpNow: 'NÅ',
  catchUpGo: 'BLI MED MANNSKAPET',
  eraStory: (chapter) => NO_STORY[chapter],
  jumpWatch: 'LEGG FRA DEG TELEFONEN · SE PÅ DEN STORE SKJERMEN',

  gamesList: 'SPILLENE I DENNE TIDSALDEREN · TRYKK PÅ DITT',
  rowMine: 'DEG · TRYKK FOR Å SPILLE',
  rowExtra: 'DEG · EKSTRA SPILL · TRYKK FOR Å SPILLE',
  rowWatch: (name) => `${name} · TRYKK FOR Å SE PÅ`,
  rowNobody: 'INGEN',
  rowPlay: 'SPILL',
  rowDone: 'FERDIG',
  finishFirst: 'Fullfør ditt eget spill først, så kan du se på andre.',
  era1Eyebrow: 'TIDSALDER 1 · JORDA',
  era1Title: 'VI FORLATER JORDA',
  era1Lede: 'Spill spillet ditt i lista under. Hvert spill som blir ferdig, lader skipet.',
  era2Eyebrow: 'TIDSALDER 2 · BOOMEN',
  era2Title: 'DEN PERFEKTE BYEN',
  era2Lede: 'Spill spillet ditt i lista under. Sammen bygger dere byen.',
  era2You: 'DEG',
  era3Eyebrow: 'TIDSALDER 3 · KRIGEN',
  era3Title: 'EN BY SOM SLUKNER',
  era3Lede: 'Spill spillet ditt i lista under. Hvert spill som blir ferdig, lader skipet.',
  era4Eyebrow: 'TIDSALDER 4 · UNDER SANDEN',
  era4Title: 'I MØRKET',
  era4Lede: 'Spill spillet ditt i lista under. Skipet går på dere alle.',
  era5Eyebrow: 'TIDSALDER 5 · TILBAKE PÅ OVERFLATEN',
  era5Title: 'TILBAKE I SOLA',
  era5Lede: 'Spill spillet ditt i lista under. Skipet er nesten hjemme.',


  gameMine: 'SPILLET DITT',
  gameWatching: (name) => `DU SER PÅ ${name}`,
  closeGame: 'Lukk',
  instruction: (def) => {
    switch (def.type) {
      case 'THREAD':
        return `Trykk for å fly opp. Fly gjennom ${def.target} åpninger.`;
      case 'RHYTHM':
        return `Begge tomlene: trykk VENSTRE eller HØYRE når lyset når streken. ${def.target} treff.${def.stutter ? ' Svake lys teller også.' : ''}`;
      case 'VINE':
        return `Sveip eller bruk pilene. Samle ${def.seeds} frø.${def.walls ? ' Unngå kanten.' : ''}`;
      case 'ECHO':
        return `Tre runder: se ${def.length}, ${def.length + 1}, så ${def.length + 2} lys, stadig raskere. Trykk dem i samme rekkefølge.${def.corrupted ? ' Gjett det grå.' : ''}`;
      case 'RECALL':
        return `Snu to kort om gangen. Finn ${def.pairs} par.${def.burnAfterMs !== null ? ' Kort brenner opp.' : ''}`;
      case 'DIVIDE':
        return `Stolpene glir. Del hver i ${def.shares} like deler: trykk der du vil kutte.`;
      case 'FORAGE':
        return `Dra fingeren for å lyse opp mørket. Plukk ${def.count} sopp.`;
      case 'PLAY':
        return thirtyWords(def, 'no')?.instruction ?? def.hint;
      case 'RELAY':
        return 'Vis koden din til gjesten som står her. Når de leser den tilbake, trykk FERDIG.';
    }
  },
  howMove: (def) => {
    switch (def.type) {
      case 'THREAD':
        return 'Trykk hvor som helst på brettet for å løfte skipet. Slipp, så synker det sakte.';
      case 'RHYTHM':
        return 'Begge tomlene. Når et lys når streken, trykk på den siden: VENSTRE eller HØYRE. Pilen viser hvilken som er neste.';
      case 'VINE':
        return 'Sveip på brettet, eller trykk på pilene, for å svinge. Styr inn på hvert frø.';
      case 'ECHO':
        return 'Lysene spilles først. Trykk så de samme lysene i samme rekkefølge. Tre runder, hver lengre og raskere.';
      case 'RECALL':
        return 'Trykk på et kort for å snu det. Finn to like.';
      case 'DIVIDE':
        return 'Stolpene glir fra side til side. Trykk på hver stolpe i det lyse feltet når det passerer: der er det rettferdige kuttet.';
      case 'FORAGE':
        return 'Dra fingeren, så lyser lyset opp mørket. Trykk på en sopp du ser.';
      case 'PLAY':
        return thirtyWords(def, 'no')?.howMove ?? def.hint;
      case 'RELAY':
        return 'Vis koden din til gjesten som står her. Når de leser den tilbake, trykk FERDIG.';
    }
  },
  howEyebrow: 'SLIK SPILLER DU',
  startGame: 'START',
  startNote: 'Tiden starter når du trykker START.',
  observerNote: (name) => `DU SER PÅ – BARE ${name} KAN SPILLE`,
  stopWatching: 'SLUTT Å SE PÅ',
  watchers: (n) => `${n} SER PÅ`,
  mistakes: (allowed) => `FEIL (${allowed} TILLATT)`,
  wellDone: 'BRA JOBBA!',
  tryAgainTitle: 'PRØV IGJEN',
  wonText: 'Ferdig! Poengene dine teller for skipet.',
  lostText: (n) => `Det var feil nummer ${n}. Begynn på nytt – tiden din går videre.`,
  done: 'FERDIG',
  later: 'SENERE',
  close: 'LUKK',
  livePlaying: (name) => `${name} SPILLER · DIREKTE`,
  paused: 'PAUSE',

  threadCount: (n, of) => `${n} / ${of} ÅPNINGER`,
  threadTap: 'TRYKK FOR Å FLY OPP',
  rhythmLane: (n) => `FELT ${n}`,
  rhythmHits: (n, of) => `${n} / ${of} TREFF`,
  rhythmFaint: 'NOEN LYS BLIR SVAKE – TRYKK PÅ DEM OGSÅ',
  rhythmTap: 'TRYKK PÅ SIDEN DER LYSET NÅR STREKEN',
  rhythmSide: (lane, lanes) => (lane === 0 ? 'VENSTRE' : lane === lanes - 1 ? 'HØYRE' : 'MIDTEN'),
  rhythmThumbs: 'BRUK BEGGE TOMLENE',
  echoWatch: 'SE…',
  echoGrey: 'GRÅTT – GJETT DETTE',
  echoWrong: 'FEIL – SE IGJEN',
  echoYourTurn: 'DIN TUR – TRYKK I SAMME REKKEFØLGE',
  echoGreyWas: (n) => `LYS ${n} VAR GRÅTT – GJETT DET`,
  echoRound: (n, of) => `RUNDE ${n}/${of}`,
  echoNextRound: (n) => `RUNDE ${n} – LENGRE, RASKERE`,
  recallPairs: (n, of) => `${n} / ${of} PAR`,
  recallBurning: 'SKYND DEG – KORT BRENNER OPP',
  vineSeeds: (n, of) => `${n} / ${of} FRØ`,
  vineHow: 'SVEIP ELLER BRUK PILENE',
  divideHead: (shares) => `DEL HVER STOLPE I ${shares} LIKE DELER`,
  divideCut: (n, of) => `KUTT ${n} AV ${of}`,
  divideEqual: '✓ LIKT',
  divideDone: (n, of) => `${n} / ${of} FERDIG`,
  divideHint: 'treff det lyse feltet mens det glir',
  forageFound: (n, of) => `${n} / ${of} SOPP`,
  forageHow: 'DRA FOR Å LYSE · TRYKK PÅ EN SOPP FOR Å PLUKKE DEN',
  relayShowTo: 'VIS DENNE KODEN TIL',
  relayWho: (name, seat) => (name ? `${name} · SETE ${seat}` : `SETE ${seat}`),
  relayThen: 'De leser koden tilbake til deg. Trykk så FERDIG.',
  relayDone: 'FERDIG – DE LESTE DEN TILBAKE',

  resultsKicker: 'SLUTT · RESULTATENE DINE',
  resultsTitle: 'RESULTATENE DINE',
  role: 'ROLLE',
  roleSaboteur: 'SABOTØR',
  roleCrew: 'MANNSKAP',
  uprisingLine: 'BOOMEN · SABOTØRENE SATTE MASKINENE MOT MENNESKENE',
  cameHome: (damage) =>
    damage === 0 ? 'SKIPET KOM HJEM UTEN EN SKRAMME' : `SKIPET KOM HJEM MED ${damage} ${damage === 1 ? 'SKADE' : 'SKADER'}`,
  thanksSaboteurs: 'Takk til sabotørene – de gjorde kvelden morsommere.',
  saveCard: '↓ LAGRE RESULTATKORTET DITT',
  savingCard: 'LAGER KORTET DITT…',
  savedCard: '✓ LAGRET',
  saveCardSub: 'et bilde å ta vare på eller dele',
  saveCardFailed: 'Telefonen ville ikke lagre det. Et skjermbilde fungerer også.',
  next: 'NESTE',
  pointsLine: (points, rank) => (rank ? `${points} poeng · ${rank}` : `${points} poeng`),
  gamesLine: (n) => `fullførte ${n} spill`,
  jobLine: (job) => `jobb: ${job}`,
  answerKicker: 'SVARET DERE TAR MED HJEM',
  answerTitle: 'DENNE KVELDEN. DISSE MENNESKENE. NÅ.',
  answerBody: 'Det beste vi fant der ute, er det dere allerede har.',
  scaleEvening: 'KVELDEN DIN',
  scaleEveningValue: '3 TIMER',
  scaleVoyage: 'REISEN',
  scaleVoyageValue: '765 ÅR',
  scaleUniverse: 'UNIVERSET',
  scaleUniverseValue: '13,8 MILLIARDER ÅR',
  homeKicker: 'VI ER HJEMME · SLUTT',
  thanksTitle: 'TAKK FOR AT DU REISTE MED SS AURORA',
  thanksBody: 'Syv hundre og sekstifem år på én kveld. Skipet er hjemme, og det er du også.',
  safeTravels: (name) => `GOD TUR VIDERE, ${name}`,
  backToResults: 'TILBAKE TIL RESULTATENE',
  cardTitle: 'SS AURORA · MIN KVELD',
  cardSub: 'ÉN KVELD · 765 ÅR',
  cardFooter: '765 ÅR · OG DET BESTE VAR I KVELD',

  somethingWrong: 'Noe gikk galt – prøv igjen.',
};

export const SCREEN_COPY: Record<Lang, ScreenCopy> = { en, no };

export function screenText(lang: Lang): ScreenCopy {
  return SCREEN_COPY[lang];
}

/** A guest's post in this language, or nothing before they have one. */
export function postName(profession: Profession | null | undefined, lang: Lang): string {
  return profession ? SCREEN_COPY[lang].post[profession] : '';
}
